import {
  faArrowLeft,
  faComments,
  faCompress,
  faExpand,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TabButton } from "../components/Tabs";
import { boundsForList, fitBoundsIfRelevant } from "../map/util";
import { useAccount, useCaMap, useProjects } from "../state";
import { ParcelRow } from "./ParcelRow";
import { ProjectChat } from "./ProjectChat";
import { ProjectFiles } from "./ProjectFiles";
type Tab = "parcels" | "files";

export const ProjectBar = () => {
  const { projects, selectedProjectId, selectProject } = useProjects();
  const workspace = useAccount((s) => s.user?.features?.workspace);
  const map = useCaMap();
  const navigate = useNavigate();
  const deselectProject = useCallback(() => selectProject(null), [selectProject]);
  const [tab, setTab] = useState<Tab>("parcels");
  const [expanded, setExpanded] = useState(false);

  const project = projects?.find((p) => p.id === selectedProjectId);
  if (!project) return null;

  const parcels = project.parcels || [];
  const area = parcels.reduce((sum, p) => (p.area || 0) + sum, 0);

  const sizeClass = expanded
    ? "left-2 sm:left-[33rem] right-2 top-16 bottom-2 overflow-hidden"
    : "right-2 top-16 w-96 h-[60vh] min-w-[280px] max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] resize overflow-hidden";

  return (
    <div
      className={`fixed z-10 ${sizeClass} bg-white rounded-lg shadow-lg border border-gray-200`}
      style={!expanded ? { direction: "rtl" } : undefined}
    >
      <div className="flex flex-col w-full h-full font-Gilroy text-sm" style={{ direction: "ltr" }}>
        <div className="flex items-center justify-between p-3 pb-0">
          <div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                selectProject(project.id);
                navigate(`/projects/${project.id}`);
              }}
              className="hover:green-blue-700 pr-2"
              title="Open projectrapport"
              aria-label="Open projectrapport"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <button
              onClick={() => fitBoundsIfRelevant(map, boundsForList(parcels))}
              className="font-bold text-black hover:text-blue-500 truncate text-left"
            >
              {project.name} ({area.toFixed()} m²)
            </button>
          </div>
          <div className="flex items-center">
            {workspace && (
              <button
                onClick={() => navigate(`/workspace/${project.id}`)}
                className="text-gray-400 hover:text-black p-1"
                title="Open workspace"
                aria-label="Open workspace"
              >
                <FontAwesomeIcon icon={faComments} />
              </button>
            )}
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-gray-400 hover:text-black p-1"
              title={expanded ? "Verkleinen" : "Vergroten"}
              aria-label={expanded ? "Verkleinen" : "Vergroten"}
            >
              <FontAwesomeIcon icon={expanded ? faCompress : faExpand} />
            </button>
            <button
              onClick={deselectProject}
              className="text-gray-400 hover:text-black p-1"
              aria-label="Sluiten"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
        <div>
          <div className="flex border-bgap-1">
            <TabButton active={tab === "parcels"} onClick={() => setTab("parcels")}>
              Percelen <span className="text-gray-400 font-normal">({parcels.length})</span>
            </TabButton>
            <TabButton active={tab === "files"} onClick={() => setTab("files")}>
              Bestanden
            </TabButton>
          </div>
        </div>
        <div className="overflow-y-auto min-h-0 flex-1">
          {tab === "parcels" ? (
            parcels.length === 0 ? (
              <p className="text-gray-400 text-xs p-2">
                Klik op een perceel op de kaart en voeg het toe
              </p>
            ) : (
              <ul className="space-y-1 p-2">
                {parcels.map((parcel) => (
                  <ParcelRow key={parcel.id} parcel={parcel} projectId={project.id} />
                ))}
              </ul>
            )
          ) : (
            <ProjectFiles projectId={project.id} />
          )}
        </div>
        {parcels.length > 0 && <ProjectChat projectId={project.id} />}
      </div>
    </div>
  );
};
